import React, { useEffect, useState } from 'react';
import API from '../../services/api';
import ToolBar from '../../components/toolbar';
import { useDispatch, useSelector } from 'react-redux';
import { setAlert } from '../../redux/dashboard/actions';
import { getSelectedCompany } from '../../redux/company/actions';
import { getSelectedProject } from '../../redux/projects/actions';
import { useDebouncedCallback } from 'use-debounce';
import CampaignTable from './campaignsTable';
import Wizard from '../../components/wizard';

import utils from '../../utils';
import './styles.scss';
import creationSteps from './creationSteps';
import ManageCampaign from './modal/manageCampaign';

const CampaignsContainer = ({ lastUpdated }) => {
  const [lastChanged, setLastChanged] = useState(lastUpdated);
  const [createMode, setCreateMode] = useState(false);

  const [newCampaign, setNewCampaign] = useState({});

  //Table states
  const [itemsCount, setItemsCount] = useState(0);

  //Drawer states
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  //Search and filters states
  const [searchingBy, setSearchingBy] = useState('');

  const company = useSelector(getSelectedCompany);
  const dispatch = useDispatch();
  const project = useSelector(getSelectedProject);

  useEffect(() => {
    setSearchingBy('');
  }, []);

  useEffect(() => {
    setSearchingBy('');
  }, [lastChanged]);

  useEffect(() => {
    setLastChanged(lastUpdated);
  }, [lastUpdated]);

  const handleSearch = useDebouncedCallback(term => {
    setSearchingBy(term);
  }, 300);

  const handleTableRowClick = campaign => {
    setSelectedCampaign(campaign);
    setShowFilters(true);
  };

  const handleCreation = dataObject => {
    const { campaign, portal } = dataObject;
    if (!portal) {
      createCampaign({ ...campaign, brandPortalId: '' });
    } else {
      if (portal.method === 'create') {
        API.createPortal({ displayName: portal.displayName, companyId: company.id })
          .then(response => {
            createCampaign({ ...campaign, brandPortalId: response.id });
          })
          .catch(error => {
            dispatch(setAlert(utils.generateAlert('Error creating portal.', 'error')));
            console.error('Error creating portal:', error);
            setLastChanged(new Date().toISOString());
          });
      } else if (portal.method === 'select') {
        createCampaign({ ...campaign, brandPortalId: portal.selectedPortal.value });
      }
    }
  };

  const createCampaign = campaignData => {
    const campaignObject = {
      companyId: company.id,
      displayName: campaignData.campaignName,
      startTime: campaignData.startDate,
      endTime: campaignData.endDate,
      brandPortalId: campaignData.brandPortalId || ''
    };
    const sitesIds = campaignData.selectedSites.map(site => site.id);

    API.createCampaign(project.companyId, campaignObject)
      .then(campaign => {
        const campaignId = campaign.id;
        if (sitesIds.length > 0) {
          API.assignSitesToCampaign(project.companyId, campaignId, sitesIds)
            .then(() => {
              dispatch(setAlert(utils.generateAlert('Campaign created and sites assigned successfully!', 'success')));
            })
            .catch(error => {
              dispatch(setAlert(utils.generateAlert('Campaign created but failed to assign sites.', 'error')));
              console.error('Error assigning sites to campaign:', error);
            });
        } else {
          dispatch(setAlert(utils.generateAlert('Campaign created successfully!', 'success')));
        }
      })
      .catch(error => {
        dispatch(setAlert(utils.generateAlert('Error creating campaign.', 'error')));
        console.error('Error creating campaign:', error);
      })
      .finally(() => {
        setLastChanged(new Date().toISOString());
        setCreateMode(false);
        setNewCampaign({});
      });
  };

  const renderCampaigns = () => {
    return (
      <>
        <div id="campaigns-table">
          <CampaignTable
            project={project}
            searchBy={searchingBy}
            isItemDetailsClosed={!showFilters}
            onItemSelected={handleTableRowClick}
            onCampaignCountChanged={setItemsCount}
            lastUpdated={lastChanged}
          />
        </div>
      </>
    );
  };

  const formatNewCampaingObject = () => {
    const campaign = {
      campaignName: newCampaign.campaignName,
      startDate: new Date(newCampaign.startDate).toISOString(),
      endDate: newCampaign.isOngoingCampaign
        ? new Date(9999, 11, 31).toISOString()
        : new Date(newCampaign.endDate).toISOString(),
      selectedSites: newCampaign.selectedSites,
      isOngoingCampaign: newCampaign.isOngoingCampaign
    };

    let portal = {
      method: newCampaign.method,
      displayName: newCampaign.method === 'create' ? newCampaign.displayName : null,
      selectedPortal: newCampaign.method === 'select' ? newCampaign.selectedPortal : null
    };

    if (portal.displayName === '' && portal.selectedPortal === null) {
      portal = null;
    }

    handleCreation({ campaign, portal });
  };

  if (createMode) {
    return (
      <div id="campaign-creation">
        <Wizard
          title={'Create a new campaign'}
          icon={'campaignsDark'}
          steps={creationSteps(newCampaign, setNewCampaign)}
          onSubmit={formatNewCampaingObject}
          onCancel={() => {
            setCreateMode(false);
            setNewCampaign({});
          }}
        />
      </div>
    );
  }

  return (
    <div className="campaigns">
      <ToolBar
        title={'Campaigns'}
        icon={'campaignsDark'}
        searchPlaceholder={'Search by campaign name'}
        onSearch={handleSearch}
        searchingBy={searchingBy}
        hasMultipleButtons
        count={itemsCount}
        buttonsList={[{ title: 'Create Campaign', classes: 'btn-primary', handleClick: () => setCreateMode(true) }]}
      />

      <div className="campaigns-container">
        <div className="d-flex flex-fill">{renderCampaigns()}</div>
        <ManageCampaign
          campaign={selectedCampaign}
          reloadCallback={() => {
            setLastChanged(new Date().toISOString());
            setShowFilters(false);
          }}
          isOpen={showFilters}
          onCancel={() => {
            setSelectedCampaign({ displayName: 'Test' });
            setShowFilters(false);
          }}
        />
      </div>
    </div>
  );
};

export default CampaignsContainer;
